import React, {
	createContext,
	ReactNode,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { Moment } from 'moment';
import {
	Currencies,
	DashboardPeriods,
	DashboardTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { useAuth } from '@client/lib/auth';
import { updateUser } from '@client/lib/api';

type Period = Values<typeof DashboardPeriods>;

export type DashboardComponent<Periods extends Period = Period> = React.FC<{
	period: Periods;
	setLastUpdated: React.Dispatch<SetStateAction<Moment | undefined>>;
	options?: DashboardOptions;
}>;

export type DashboardOptions = {
	eua?: boolean;
};

export type Dashboard<Periods extends Period> = {
	key: Values<typeof DashboardTypes>;
	label: string;
	periodOptions: Periods[];
	enableCurrencyFilter?: boolean;
	options?: DashboardOptions;
	component: DashboardComponent<Periods>;
	enableCache?: boolean;
	refresh: (
		dashboardType: Values<typeof DashboardTypes>,
		period: Values<typeof DashboardPeriods>
	) => void;
};

export type DashboardInfo = {
	period: Values<typeof DashboardPeriods>;
	setPeriod: (period: Values<typeof DashboardPeriods>) => void;
	setDashboard: (dashboardKey: Values<typeof DashboardTypes>) => void;
	options?: DashboardOptions;
	lastUpdated: Moment | undefined;
	setLastUpdated: React.Dispatch<SetStateAction<Moment | undefined>>;
	selectedDashboard: Dashboard<Period>;
	dashboards: Array<Dashboard<Period>>;
	DashboardComponent: DashboardComponent<Period>;
	enableCache?: boolean;
};

export type DashboardCurrencyContextValue = {
	currency: Values<typeof Currencies> | null;
	setCurrency: (c: Values<typeof Currencies>) => void;
	availableCurrencies: Array<Values<typeof Currencies>>;
	setAvailableCurrencies: (c: Array<Values<typeof Currencies>>) => void;
};

const DashboardPeriodContext = createContext<Values<typeof DashboardPeriods> | null>(null);
const DashboardCurrencyContext = createContext<DashboardCurrencyContextValue | null>(null);

// This may be used in widgets that rely on the dashboard currency for fetching data
export const useDashboardCurrency = () => useContext(DashboardCurrencyContext);
// This may be used in widgets that rely on the dashboard period for fetching data
export const useDashboardPeriod = () => useContext(DashboardPeriodContext);

type DashboardInfoProviderProps = {
	dashboards: DashboardInfo['dashboards'];
	children: (options: DashboardInfo) => ReactNode;
};

const DashboardInfoProvider: React.FC<DashboardInfoProviderProps> = ({ children, dashboards }) => {
	const { userInfo, refreshAuthInfo } = useAuth();

	const initialDashboard = userInfo.defaultDashboard || userInfo.selectedDashboard || dashboards[0].key;

	const [activeDashboard, setActiveDashboard] = useState(initialDashboard);
	const [lastUpdated, setLastUpdated] = useState<Moment | undefined>();

	const selectedDashboard = dashboards.find((d) => d.key === activeDashboard);

	const periods = useMemo(
		() =>
			selectedDashboard?.periodOptions ??
			(['quarter', 'year', 'hundredtwentydays', 'sixtydays'] as Period[]),
		[selectedDashboard?.periodOptions],
	);

	const [period, setPeriod] = useState<Values<typeof DashboardPeriods>>(periods[0]);

	const defaultCurrency = (userInfo?.baseCurrency ?? Currencies.USD) as Values<typeof Currencies>;
	const [currency, setCurrency] = useState<Values<typeof Currencies>>(defaultCurrency);
	const [availableCurrencies, setAvailableCurrencies] = useState<Array<Values<typeof Currencies>>>([
		defaultCurrency,
	]);

	useEffect(() => {
		const nextDefault = (userInfo?.baseCurrency ?? Currencies.USD) as Values<typeof Currencies>;
		setCurrency(nextDefault);
		setAvailableCurrencies([nextDefault]);
	}, [userInfo?.organizationName, userInfo?.baseCurrency]);

	useEffect(() => {
		if (!availableCurrencies.length) {
			return;
		}

		if (!availableCurrencies.includes(currency)) {
			setCurrency(availableCurrencies[0]);
		}
	}, [availableCurrencies, currency]);

	useEffect(() => {
		if (dashboards.some((d) => d.key === activeDashboard)) {
			return;
		}

		const next =
			userInfo.defaultDashboard || userInfo.selectedDashboard || dashboards[0].key;

		setActiveDashboard(next);
	}, [dashboards, activeDashboard, userInfo.defaultDashboard, userInfo.selectedDashboard]);

	useEffect(() => {
		if (!selectedDashboard) {
			return;
		}

		if (period == null || !periods.includes(period)) {
			setPeriod(periods[0] as Values<typeof DashboardPeriods>);
		}
	}, [selectedDashboard, periods, period]);

	const setDashboard = async (key: Values<typeof DashboardTypes>) => {
		setActiveDashboard(key);
		await updateUser({ attributes: { selectedDashboard: key } });
		await refreshAuthInfo();
	};

	return (
		<DashboardPeriodContext.Provider value={period}>
			<DashboardCurrencyContext.Provider
				value={{
					currency,
					setCurrency,
					availableCurrencies,
					setAvailableCurrencies,
				}}
			>
				{selectedDashboard != null &&
					(periods == null || periods.includes(period)) &&
					children({
						period,
						setPeriod,
						setDashboard,
						lastUpdated,
						setLastUpdated,
						enableCache: selectedDashboard.enableCache,
						options: selectedDashboard.options,
						selectedDashboard,
						dashboards,
						DashboardComponent: selectedDashboard.component,
					})}
			</DashboardCurrencyContext.Provider>
		</DashboardPeriodContext.Provider>
	);
};

export default DashboardInfoProvider;
